import BigNumber from "bignumber.js";

// Helper
export const toDecimals = (amount: any, decimals: any) =>
  new BigNumber(amount).multipliedBy(new BigNumber(10).pow(decimals || 18));
/**
 * Bonding Curve Price Calculation methods inspired from the contract implementation
 */

// Constants
const a = 0.001;
const k = 0.00000001;
const c = 0.0009999;
const decimals = new BigNumber(10).pow(18); // Precision scaling (10^18)

function integrateTrapezoidal(
  f: (x: number) => number,
  a: number,
  b: number,
  n = 100
) {
  const h = (b - a) / n;
  let sum = 0.5 * (f(a) + f(b));

  for (let i = 1; i < n; i++) {
    const x = a + i * h;
    sum += f(x);
  }

  return sum * h;
}

// Calculate Price Function
export function calculatePrice(
  totalSupply: BigNumber,
  count: BigNumber
): BigNumber {
  const totalSupplyInDecimals = totalSupply.div(decimals).toNumber();
  const countInDecimals = count.div(decimals).toNumber();
  const f = (x: number) => a * Math.exp(k * x) - c;
  const cumulativeTotal = integrateTrapezoidal(
    f,
    totalSupplyInDecimals,
    totalSupplyInDecimals + countInDecimals
  );
  return new BigNumber(cumulativeTotal).multipliedBy(decimals);
}

export function calculateTokensFromAE(
  totalSupply: BigNumber,
  result: number,
  tolerance = 1e-3
): BigNumber {
  const price = new BigNumber(toDecimals(result, 18).toString());
  const maxIterations = 1000;
  const f = (x: number) => a * Math.exp(k * x) - c;

  const totalSupplyInDecimals = totalSupply.div(decimals).toNumber();
  const priceInDecimals = price.div(decimals).toNumber();

  let low = 0;
  let high = 1e12; // Arbitrary upper bound for count
  let mid = 0;

  let iterations = 0;
  while (iterations < maxIterations) {
    mid = (low + high) / 2;
    const integralValue = integrateTrapezoidal(
      f,
      totalSupplyInDecimals,
      totalSupplyInDecimals + mid
    );

    if (Math.abs(integralValue - priceInDecimals) < tolerance) {
      break;
    }

    if (integralValue < priceInDecimals) {
      low = mid;
    } else {
      high = mid;
    }
    iterations++;
  }

  return new BigNumber(mid);
}

// Calculate Buy Price
export function calculateBuyPrice(
  totalSupply: BigNumber,
  count: BigNumber
): BigNumber {
  return calculatePrice(totalSupply, count);
}

// Calculate Sell Return
export function calculateSellReturn(
  totalSupply: BigNumber,
  sellTokens: BigNumber
): BigNumber {
  return calculatePrice(totalSupply.minus(sellTokens), sellTokens);
}

// Calculate Buy Price with Affiliation Fee 0.005%
export function calculateBuyPriceWithAffiliationFee(
  totalSupply: BigNumber,
  count: BigNumber
): BigNumber {
  return calculateBuyPrice(totalSupply, count).multipliedBy(1.005);
}
